import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { ArrowLeft, Save } from 'lucide-react';
import SEO from '../components/SEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function VideoFormPage() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    title: '',
    youtube_url: '',
    description: '',
    category: ''
  });
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!loading && (!user || user.role !== 'admin')) {
      navigate('/admin');
    }
  }, [user, loading, navigate]);

  useEffect(() => {
    if (isEdit && user && user.role === 'admin') {
      fetchVideo();
    }
  }, [id, user]);

  const fetchVideo = async () => {
    try {
      const { data } = await axios.get(`${API}/videos`);
      const video = data.find(v => v.id === id);
      if (video) {
        setFormData({
          title: video.title,
          youtube_url: video.youtube_url,
          description: video.description,
          category: video.category || ''
        });
      }
    } catch (error) {
      console.error('Error fetching video:', error);
      setError('Failed to load video');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);

    try {
      if (isEdit) {
        await axios.put(`${API}/videos/${id}`, formData, { withCredentials: true });
      } else {
        await axios.post(`${API}/videos`, formData, { withCredentials: true });
      }
      navigate('/admin/dashboard');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save video');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  if (loading) {
    return <div className="min-h-screen bg-[#FFF8F0] flex items-center justify-center">Loading...</div>;
  }

  if (!user || user.role !== 'admin') {
    return null;
  }

  return (
    <div className="min-h-screen bg-[#FFF8F0]" data-testid="video-form-page">
      <SEO title="Video Editor" noindex />
      <div className="bg-white border-b border-[#E5E5E5] p-6">
        <div className="max-w-[1200px] mx-auto">
          <button
            onClick={() => navigate('/admin/dashboard')}
            data-testid="back-to-dashboard-button"
            className="flex items-center gap-2 text-[#8A8A8A] hover:text-[#E63946] transition-colors mb-4"
            
          >
            <ArrowLeft size={18} />
            Back to Dashboard
          </button>
          <h1 className="text-3xl font-black tracking-tighter text-[#0A0A0A]" >
            {isEdit ? 'EDIT VIDEO' : 'ADD VIDEO'}
          </h1>
        </div>
      </div>
      
      <div className="max-w-[1200px] mx-auto p-6">
        <div className="bg-white border border-[#E5E5E5] p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-xs tracking-[0.2em] uppercase font-bold text-[#8A8A8A] mb-2" >
                VIDEO TITLE *
              </label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleChange}
                data-testid="video-title-input"
                required
                className="w-full px-4 py-3 border border-[#E5E5E5] focus:outline-none focus:border-[#E63946] transition-colors"
                
                placeholder="Enter video title"
              />
            </div>
            
            <div>
              <label className="block text-xs tracking-[0.2em] uppercase font-bold text-[#8A8A8A] mb-2" >
                YOUTUBE URL *
              </label>
              <input
                type="url"
                name="youtube_url"
                value={formData.youtube_url}
                onChange={handleChange}
                data-testid="video-youtube-url-input"
                required
                className="w-full px-4 py-3 border border-[#E5E5E5] focus:outline-none focus:border-[#E63946] transition-colors"
                
                placeholder="https://www.youtube.com/watch?v=..."
              />
            </div>
            
            <div>
              <label className="block text-xs tracking-[0.2em] uppercase font-bold text-[#8A8A8A] mb-2" >
                DESCRIPTION *
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                data-testid="video-description-input"
                required
                rows="5"
                className="w-full px-4 py-3 border border-[#E5E5E5] focus:outline-none focus:border-[#E63946] transition-colors resize-none"
                
                placeholder="Enter video description"
              />
            </div>
            
            <div>
              <label className="block text-xs tracking-[0.2em] uppercase font-bold text-[#8A8A8A] mb-2" >
                CATEGORY (OPTIONAL)
              </label>
              <select
                name="category"
                value={formData.category}
                onChange={handleChange}
                data-testid="video-category-select"
                className="w-full px-4 py-3 border border-[#E5E5E5] focus:outline-none focus:border-[#E63946] transition-colors"
                
              >
                <option value="">Select a category</option>
                <option value="violent">Violent Crimes</option>
                <option value="cyber">Cyber Crimes</option>
                <option value="white-collar">Financial</option>
                <option value="property">Family/Extramarital</option>
                <option value="tragedy">Tragedy</option>
                <option value="others">Others</option>
              </select>
            </div>
            
            {error && (
              <div className="p-3 bg-[#E63946] bg-opacity-10 border border-[#E63946] text-[#E63946] text-sm" data-testid="video-form-error" >
                {error}
              </div>
            )}
            
            <div className="flex gap-4">
              <button
                type="submit"
                disabled={saving}
                data-testid="video-save-button"
                className="flex items-center gap-2 px-6 py-3 bg-[#E63946] text-white font-bold text-sm tracking-[0.2em] uppercase hover:bg-[#d32f3e] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                
              >
                <Save size={16} />
                {saving ? 'SAVING...' : 'SAVE VIDEO'}
              </button>
              <button
                type="button"
                onClick={() => navigate('/admin/dashboard')}
                data-testid="video-cancel-button"
                className="px-6 py-3 border border-[#E5E5E5] text-[#8A8A8A] font-bold text-sm tracking-[0.2em] uppercase hover:border-[#E63946] hover:text-[#E63946] transition-colors"
                
              >
                CANCEL
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default VideoFormPage;
